'use client';

import {
  OrderLineItem,
  OrderLineItemContainerVariant,
  OrderLineItemCopy,
  OrderLineItemLead,
  OrderLineItemMedia,
  OrderLineItemName,
  OrderLineItemPrice,
  OrderLineItemQuantityRow,
  OrderLineItemVariant,
} from '@vs-shop/commerce-ui/orderLineItem';
import { cartPendingTraceVariants } from '@vs-shop/commerce-ui/cartDrawer';
import { useIntl } from 'react-intl';
import type { ReactNode } from 'react';

import type { ProductNameText } from '@vs-shop/schemas/branded/ProductSchema';
import type { Price } from '@vs-shop/schemas/primitive/price';
import type { CurrencyT } from '../../../../../config/currency';
import type { OrderItem } from '../../../../../effect/schema/branded/orderTightened';
import { IntlMoneyFromPrice } from '../../../../server/intl/Intl';
import type { CartDrawerPendingStatus } from '@vs-shop/engine/module/cart/cartDrawerProjection';
import { CartDrawerProjectedQuantity } from './CartDrawerProjectedQuantity';

type Props = {
  productName: ProductNameText;
  quantity: OrderItem['quantity'];
  totalPrice: Price;
  currency: CurrencyT;
  image: ReactNode;
  ageBadge: ReactNode;
  status: CartDrawerPendingStatus;
};

/**
 * This line shows an add before the backend confirms it.
 * It keeps the product name and price from the page.
 * The product image or placeholder stays visible inside the traced line.
 * The confirmed line replaces it after a successful read.
 * A disabled quantity control holds the confirmed line's layout.
 */
export function CartDrawerPendingItem({
  productName,
  quantity,
  totalPrice,
  currency,
  image,
  ageBadge,
  status,
}: Props) {
  const intl = useIntl();
  const statusMessage = intl.formatMessage(
    status === 'adding'
      ? { id: 'cartDrawer.pendingLine.addingQuantity', defaultMessage: 'Přidávám {quantity} ks' }
      : status === 'verifying'
        ? { id: 'cartDrawer.pendingLine.verifying', defaultMessage: 'Ověřuji košík' }
        : {
            id: 'cartDrawer.pendingLine.unresolved',
            defaultMessage: 'Změnu košíku se nepodařilo ověřit',
          },
    { quantity }
  );
  const quantityAndPrice = (
    <>
      <CartDrawerProjectedQuantity confirmedQuantity={0} pendingQuantity={quantity} />
      <OrderLineItemPrice variant={OrderLineItemVariant.DRAWER}>
        <span aria-hidden="true">≈ </span>
        <IntlMoneyFromPrice value={totalPrice} currency={currency} />
      </OrderLineItemPrice>
    </>
  );

  return (
    <OrderLineItem
      variant={OrderLineItemContainerVariant.DRAWER}
      data-pending="true"
      aria-busy={status !== 'unresolved'}
      className={status !== 'unresolved' ? cartPendingTraceVariants() : undefined}
    >
      <OrderLineItemLead variant={OrderLineItemVariant.DRAWER}>
        <OrderLineItemMedia variant={OrderLineItemVariant.DRAWER}>
          {image}
          {ageBadge}
        </OrderLineItemMedia>
        <OrderLineItemCopy variant={OrderLineItemVariant.DRAWER}>
          <div className="flex flex-wrap items-center gap-x-2">
            <OrderLineItemName variant={OrderLineItemVariant.DRAWER}>
              {productName}
            </OrderLineItemName>
            <span role="status" aria-live="polite" className="sr-only">
              {statusMessage}
            </span>
          </div>
          <OrderLineItemQuantityRow className="hidden tab:flex">
            {quantityAndPrice}
          </OrderLineItemQuantityRow>
        </OrderLineItemCopy>
      </OrderLineItemLead>
      <OrderLineItemQuantityRow className="tab:hidden">{quantityAndPrice}</OrderLineItemQuantityRow>
    </OrderLineItem>
  );
}
